import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import PixelSelector from './PixelSelector'
import FacebookPageSelector from './FacebookPageSelector'
import InstagramPageSelector from './InstagramPageSelector'
import { Info } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import AboutMetaPixel from './AboutMetaPixel'
import { InfoIcon } from '@/components/icons/InfoIcon'

const AddAccountsForm = ({
    publisher,
    setPixelId,
    userAdAccount,
    resetValidation,
    setFormValuesChanged,
    setSelectedPublisherData,
    selectedPixel,
    selectedPageId,
    selectedInstagramId,
    disabled,
    submitSettings,
    formValues,
    allInstagramAccounts,
}) => {
    const {
        control,
        handleSubmit,
        formState: { errors, isDirty },
        watch,
        setValue,
        reset,
        trigger,
    } = useForm({
        mode: 'onChange',
        defaultValues: {
            pixel: selectedPixel || '',
            facebookAccount: selectedPageId || '',
            instagramAccount: selectedInstagramId || '',
        },
    })

    const watchedValues = watch()

    useEffect(() => {
        if (setFormValuesChanged) setFormValuesChanged(isDirty)
    }, [isDirty, setFormValuesChanged])

    const setFacebookAccount = (value) => {
        setValue('facebookAccount', value)
        setSelectedPublisherData({
            page_id: value,
            pixel: watchedValues.pixel,
            instagram_id: watchedValues.instagramAccount,
        })
    }

    const setInstagramAccount = (value) => {
        setValue('instagramAccount', value)
        setSelectedPublisherData({
            page_id: watchedValues.facebookAccount,
            pixel: watchedValues.pixel,
            instagram_id: value,
        })
    }

    const setPixel = (value) => {
        setValue('pixel', value)
        setSelectedPublisherData({
            page_id: watchedValues.facebookAccount,
            pixel: value,
            instagram_id: watchedValues.instagramAccount,
        })
    }

    const onFormSubmit = async (data, event) => {
        const isValid = await trigger()

        if (isValid) {
            const formValuesForSubmission = {
                [publisher.name]: {
                    pixel: data.pixel,
                    page_id: data.facebookAccount,
                    instagram_id: data.instagramAccount,
                    label: formValues?.[publisher.name]?.label || '',
                },
            }
            submitSettings(formValuesForSubmission, publisher.name)
        }
    }

    const handleReset = () => {
        reset()
        resetValidation()
    }

    return (
        <form onSubmit={handleSubmit(onFormSubmit)} className="tw:space-y-6">
            <div className="tw:grid tw:grid-cols-3 tw:gap-12 tw:mb-1.5">
                <div className="tw:grid tw:gap-1 tw:max-w-[300px]">
                    <label
                        htmlFor="pixel"
                        className="tw:!inline-flex tw:items-center tw:gap-2 tw:text-sm tw:font-semibold tw:mb-1.5 tw:dark:text-white tw:text-[#2A3749]"
                    >
                        <span>Pixel</span>
                        <Tooltip>
                            <TooltipTrigger>
                                <Info className="tw:w-5 tw:h-5 tw:text-gray-200" />
                            </TooltipTrigger>
                            <TooltipContent showArrow={false}>
                                <div className="tw:max-w-sm">
                                    <AboutMetaPixel />
                                </div>
                            </TooltipContent>
                        </Tooltip>
                    </label>
                    <PixelSelector
                        control={control}
                        errors={errors}
                        onChange={setPixel}
                        value={watchedValues.pixel}
                        setPixelId={setPixelId}
                        pixels={userAdAccount.pixels || []}
                        disabled={disabled}
                    />
                    {userAdAccount.pixels && userAdAccount.pixels.length === 0 && (
                        <label className="tw:text-sm tw:text-error tw:mt-1.5">
                            <span className="tw:flex tw:items-center tw:gap-1">
                                <Info className="tw:w-[22px] tw:h-[22px] tw:inline-block tw:pr-1" />
                                No pixels associated with this account.
                            </span>{' '}
                            Tracking is not possible without a pixel. Use another account or go to{' '}
                            <a
                                target="_blank"
                                className="tw:font-semibold"
                                rel="noopener noreferrer"
                                href="https://business.facebook.com"
                            >
                                Meta Business Manager
                            </a>{' '}
                            to share the pixel with this account.
                        </label>
                    )}
                </div>

                <div className="tw:grid tw:gap-1 tw:max-w-[300px]">
                    <label
                        htmlFor="facebookAccount"
                        className="tw:gap-2 tw:text-sm tw:font-semibold tw:mb-1.5 tw:dark:text-white tw:text-[#2A3749]"
                    >
                        <span>Facebook Page</span>
                    </label>

                    <FacebookPageSelector
                        control={control}
                        errors={errors}
                        onChange={setFacebookAccount}
                        value={watchedValues.facebookAccount}
                        pages={userAdAccount.pages || []}
                        disabled={disabled}
                    />
                </div>

                <div className="tw:grid tw:gap-1 tw:max-w-[300px]">
                    <label
                        htmlFor="instagramAccount"
                        className="tw:text-sm tw:font-semibold tw:mb-1.5 tw:dark:text-white tw:text-[#2A3749]"
                    >
                        <span>Instagram Account</span>
                    </label>

                    <InstagramPageSelector
                        control={control}
                        errors={errors}
                        onChange={setInstagramAccount}
                        value={watchedValues.instagramAccount}
                        pages={allInstagramAccounts || []}
                        disabled={disabled}
                    />
                </div>
                <div className="tw:hidden">
                    <label
                        htmlFor="pixel"
                        className="tw:gap-2 tw:text-sm tw:font-semibold tw:mb-1.5 tw:dark:text-white tw:text-[#2A3749]"
                    >
                        <span>Instagram Page</span>
                    </label>
                    <InstagramPageSelector
                        control={control}
                        errors={errors}
                        onChange={(value) => setValue('instagramAccount', value)}
                        value={watchedValues.instagramAccount}
                        pages={allInstagramAccounts || []}
                        disabled={disabled}
                    />
                </div>
            </div>
            <div className="tw:grid tw:grid-cols-3 tw:gap-12">
                <div
                    className={`tw:text-[13px] tw:col-span-2 tw:text-yellow-300 tw:flex tw:gap-1 tw:items-center ${
                        selectedPageId && selectedPixel ? 'tw:opacity-0' : ''
                    }`}
                >
                    <InfoIcon className="tw:w-4 tw:h-4" />
                    <span>Select a Pixel and a Facebook Page to proceed with creating your ads and campaigns</span>
                </div>
                <a
                    target="_blank"
                    rel="noopener noreferrer"
                    className="tw:text-sm tw:font-semibold tw:hidden"
                    href="https://www.facebook.com/business/help/connect-instagram-to-page"
                >
                    How to Connect an Instagram page
                </a>
            </div>

            {!disabled && (
                <div className="tw:flex tw:justify-end tw:gap-3 tw:pt-4">
                    <Button variant="secondary" className="tw:w-[156px] tw:!text-sm" onClick={handleReset}>
                        Cancel
                    </Button>
                    <Button type="submit" className="tw:w-[156px] tw:!text-sm">
                        Save
                    </Button>
                </div>
            )}
        </form>
    )
}

export default AddAccountsForm
